import { devWarn } from '../utils/devLog';
/**
 * GeocodingService - Centralized geocoding utilities
 * Single source of truth for address formatting and reverse geocoding
 */

export interface NominatimAddress {
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  county?: string;
  state?: string;
  country?: string;
  // Additional fields Nominatim may return
  hamlet?: string;
  suburb?: string;
  district?: string;
  region?: string;
  city_district?: string;
}

export interface ReverseGeocodeResult {
  shortAddress: string;
  fullAddress: string;
  address?: NominatimAddress;
}

/**
 * Extract city and country from Nominatim address
 * Priority: city > town > village > municipality > county > state
 */
export const formatShortAddress = (address?: NominatimAddress): string => {
  if (!address) return '';

  const city = address.city || address.town || address.village ||
               address.municipality || address.county || address.state || '';
  const country = address.country || '';

  if (city && country) {
    return `${city}, ${country}`;
  }
  return city || country || '';
};

/**
 * Format address for rough/privacy mode: City, Country (no state/oblast)
 */
export const formatRoughAddress = (address?: NominatimAddress): string => {
  if (!address) return '';

  // Nominatim may use different fields for city depending on location
  const city = address.city || address.town || address.village ||
               address.municipality || address.city_district ||
               address.district || address.suburb || address.hamlet || '';
  const country = address.country || '';

  const parts = [city, country].filter(Boolean);
  return parts.join(', ');
};

/**
 * Extract city name from Nominatim address
 */
export const extractCityName = (address?: NominatimAddress): string => {
  if (!address) return '';
  return address.city || address.town || address.village ||
         address.municipality || address.city_district ||
         address.district || address.suburb || address.hamlet || '';
};

/**
 * Search for city coordinates by name
 * Returns the center coordinates of the city
 */
export const searchCityCoordinates = async (
  cityName: string
): Promise<{ lat: number; lng: number } | null> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityName)}&limit=1&addressdetails=1`
    );

    if (!response.ok) {
      throw new Error('City search failed');
    }

    const data = await response.json();
    if (data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    }
    return null;
  } catch (error) {
    devWarn('City search error:', error);
    return null;
  }
};

/**
 * Reverse geocode coordinates to get address
 * Returns both short (City, Country) and full address
 */
export const reverseGeocode = async (
  lat: number,
  lng: number
): Promise<ReverseGeocodeResult> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
    );

    if (!response.ok) {
      throw new Error('Reverse geocoding failed');
    }

    const data = await response.json();
    const shortAddress = formatShortAddress(data.address) || data.display_name || '';

    return {
      shortAddress,
      fullAddress: data.display_name || '',
      address: data.address
    };
  } catch (error) {
    devWarn('Reverse geocoding error:', error);
    return {
      shortAddress: '',
      fullAddress: '',
      address: undefined
    };
  }
};

/**
 * Get current location with reverse geocoding
 * Returns coordinates and formatted address
 */
export const getCurrentLocationWithAddress = (): Promise<{
  latitude: number;
  longitude: number;
  shortAddress: string;
  fullAddress: string;
}> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const geocodeResult = await reverseGeocode(latitude, longitude);

        resolve({
          latitude,
          longitude,
          shortAddress: geocodeResult.shortAddress,
          fullAddress: geocodeResult.fullAddress
        });
      },
      (error) => {
        reject(new Error(`Geolocation error: ${error.message}`));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  });
};

export default {
  formatShortAddress,
  formatRoughAddress,
  extractCityName,
  reverseGeocode,
  searchCityCoordinates,
  getCurrentLocationWithAddress
};
